/**
 * POP Authority setup form schema (design: pa-setup.jsx OnboardingForm + PublishingStep).
 *
 * Rendered by the White Glove `FormStep.vue` renderer through its `schema` / `sections` props.
 * Section indexes and field keys are kept aligned with the White Glove audit form so the backend
 * keeps reading `step0.location_code / language_code`, `step1.target_country / target_cities`
 * and `step2.need_assistance_publishing` unchanged (Asana silo tasks, publishing mode).
 */
import { required, email, url, helpers } from '@vuelidate/validators'
import languages from '@/consts/dataforseo_languages.json'
import dataForSeoLocations from '@/consts/dataforseo_locations.json'
import { oneOf } from '@/components/SitesSingleView/WhiteGlove/SiteContentAuditWidget/FormStep.vue'

/** Required only while the sibling field `key` equals `expected` (conditional publishing questions). */
export const requiredWhen = (key, expected) => helpers.withMessage(
  'This field is required',
  helpers.withParams(
    { type: 'requiredWhen', key, expected },
    (value, siblings) => ((siblings && siblings[key] === expected) ? helpers.req(value) : true)
  )
)

/** Checkbox that must be ticked while the sibling field `key` equals `expected` (the publishing agreement). */
export const acceptedWhen = (key, expected) => helpers.withMessage(
  'Please agree to the Website Publishing Agreement to continue.',
  helpers.withParams(
    { type: 'acceptedWhen', key, expected },
    (value, siblings) => ((siblings && siblings[key] === expected) ? value === true : true)
  )
)

/** Sections rendered by each wizard step (indexes into the full schema). */
export const POP_AUTHORITY_FORM_SECTIONS = {
  SITE_INFO: [0, 1],
  PUBLISHING: [2],
}

export function locationNameForCode (code) {
  const location = dataForSeoLocations.locations.find(l => String(l.location_code) === String(code))
  return location ? location.location_name : ''
}

export function languageNameForCode (code) {
  const language = languages.find(l => l.language_code === code)
  return language ? language.language_name : ''
}

export function makePopAuthorityFormSchema () {
  return [
    {
      type: 'section',
      label: 'Site info',
      description: 'Your website, location and language',
      fields: [
        {
          type: 'field_row',
          fields: [
            {
              type: 'input',
              key: 'email',
              label: 'Your email',
              placeholder: 'name@example.com',
              readonly: true,
              validation: { required, email },
            },
            {
              type: 'input',
              key: 'site_url',
              label: 'Your site URL',
              placeholder: 'https://',
              validation: { required, url },
            },
          ],
        },
        {
          type: 'field_row',
          fields: [
            {
              type: 'autocomplete',
              key: 'location_code',
              label: 'Location',
              placeholder: '',
              autocompleteItems: dataForSeoLocations.locations,
              itemText: 'location_name',
              itemValue: 'location_code',
              validation: { required },
            },
            {
              type: 'autocomplete',
              key: 'language_code',
              label: 'Language',
              placeholder: '',
              autocompleteItems: languages,
              itemText: 'language_name',
              itemValue: 'language_code',
              validation: { required },
            },
          ],
        },
        {
          type: 'radio',
          key: 'site_type',
          label: 'What type of website is this?',
          placeholder: '',
          validation: {
            required,
            oneOf: oneOf(['local_business', 'ecommerce', 'saas', 'marketplace', 'other']),
          },
          options: [
            { key: 'local_business', label: 'Local business' },
            { key: 'ecommerce', label: 'eCommerce' },
            { key: 'saas', label: 'SaaS' },
            { key: 'marketplace', label: 'Marketplace' },
            { key: 'other', label: 'Other' },
          ],
          initial_value: 'local_business',
        },
      ],
    },

    {
      type: 'section',
      label: 'General website info',
      description: 'Tell us about your website and competitors',
      fields: [
        {
          type: 'textarea',
          key: 'close_industry_competitors',
          label: 'Could you provide 2/3 close industry competitors?',
          placeholder: 'Competitor URLs, one per line',
          validation: { required },
        },
        {
          type: 'textarea',
          key: 'keywords_topics_to_focus_on',
          label: 'Anything that must be covered in the audit? (pages, products, services, keywords, topics)',
          placeholder: 'One per line',
        },
        {
          type: 'textarea',
          key: 'new_top_level_topics_to_research',
          label: 'New topics you want us to explore?',
          placeholder: 'Topics, one per line',
        },
        {
          type: 'textarea',
          key: 'parts_not_to_focus_on',
          label: 'Anything we should stay away from? (pages, categories, topics)',
          placeholder: 'Description',
        },
        {
          type: 'field_row',
          fields: [
            {
              type: 'input',
              key: 'godd_piece_of_content_url',
              label: 'Example of a good piece of content on your site (URL)',
              placeholder: 'https://',
              validation: { url },
            },
            {
              type: 'input',
              key: 'target_cities',
              label: 'Specific cities / suburbs to target?',
              placeholder: 'List cities or suburbs',
            },
          ],
        },
        {
          type: 'radio',
          key: 'approach',
          label: 'Guidance for audit approach',
          extra_info_html: `
          <div>Please select your preference for how we should conduct the analysis:</div>
          `,
          placeholder: '',
          validation: {
            required,
            oneOf: oneOf(['data_driven', 'user_specified_prioritites_only']),
          },
          special_label_style: true,
          options: [
            {
              key: 'data_driven',
              label: 'Data-driven analysis with priority consideration',
              desc_html: '<div>We will analyze data to uncover the best opportunities and incorporate your priorities. This approach balances data-driven insights with areas you have identified as essential.</div>',
            },
            {
              key: 'user_specified_prioritites_only',
              label: 'User-specified priorities only',
              desc_html: '<div>We will solely focus on the areas you\'ve specified as essential, disregarding additional opportunities identified through data analysis.</div>',
            },
          ],
          initial_value: 'data_driven',
        },
        {
          type: 'radio',
          key: 'existing_new_opportunities',
          label: 'Existing vs. New opportunities',
          extra_info_html: `
          <div>Our system prioritizes existing opportunities when a site has enough pages. However, if you are looking for new opportunities beyond your current scope, we can provide recommendations. Would you like us to focus on existing opportunities, or would you prefer to explore new ones too?</div>
          `,
          placeholder: '',
          validation: {
            required,
            oneOf: oneOf(['identify_best', 'focus_existing', 'mix']),
          },
          options: [
            { key: 'identify_best', label: 'Identify the best opportunities based on data analysis' },
            { key: 'focus_existing', label: 'Focus on existing opportunities only' },
            { key: 'mix', label: 'Provide a mix of existing and new opportunities' },
          ],
          initial_value: 'identify_best',
        },
        {
          // Filled from the step0 location name at submit (FormStep.fillDerivedFields); read by Asana silo tasks.
          type: 'hidden',
          key: 'target_country',
          label: 'Target country',
          derive: 'location_name',
          initial_value: '',
        },
      ],
    },

    {
      type: 'section',
      label: 'Publishing',
      description: 'How you publish content on your site',
      fields: [
        {
          type: 'radio',
          key: 'need_assistance_publishing',
          label: 'Do you need assistance getting content published on the site?',
          placeholder: '',
          validation: {
            required,
            oneOf: oneOf(['yes', 'no']),
          },
          special_label_style: true,
          options: [
            {
              key: 'yes',
              label: 'Yes, please publish for us',
              desc_html: '<div>Please read the Website Publishing Agreement below (opens when clicked).</div>',
            },
            { key: 'no', label: 'No, I would like to publish content myself.' },
          ],
        },
        {
          type: 'info',
          title: 'You publish, we deliver',
          icon: 'outbox',
          html: 'When your content is ready we will send it to you in Google Docs, together with any relevant instructions (page placement, internal links, metadata). You publish it on your site whenever you are ready.',
          show_if: { key: 'need_assistance_publishing', value: 'no' },
        },
        {
          type: 'textarea',
          key: 'process_to_get_content_published',
          label: 'What is the process for us to get content published on your site? (e.g. Do we get access and permission to publish? Or is there an approval process?)',
          placeholder: 'Example: Login to our website admin area and using the page template ‘Service page’ create the page and save as draft. Send the link to the draft page to ‘name@business.com’ for us to approve and publish.',
          required_mark: true,
          validation: { requiredWhen: requiredWhen('need_assistance_publishing', 'yes') },
          show_if: { key: 'need_assistance_publishing', value: 'yes' },
        },
        {
          type: 'textarea',
          key: 'publishing_templates',
          label: 'Do you have templates you would like used for new target and supporting pages?',
          tooltip: 'A target page is usually a service page, landing page or product page. A supporting page is usually a blog post. Different site pages use different layouts and designs.\nSo list the templates or pages on your site you would like us to create your new target and supporting pages. Feel free to give us a Loom link to give us instructions.',
          placeholder: 'Links or description',
          show_if: { key: 'need_assistance_publishing', value: 'yes' },
        },
        {
          type: 'input',
          key: 'cms_used',
          label: 'What web builder or CMS is being used?',
          placeholder: 'e.g. WordPress',
          required_mark: true,
          validation: { requiredWhen: requiredWhen('need_assistance_publishing', 'yes') },
          show_if: { key: 'need_assistance_publishing', value: 'yes' },
        },
        {
          // "Add us as an editor on your site" box (design update 2026-10-13; PublishingStep.vue renders it
          // through the FormStep slot `field-publishing_access`). The answer is
          // { method: granted|here|later, login_url, username, password }: the backend strips the password,
          // keeps it encrypted on the site and stores `password_set` instead. `initial_value` stays null:
          // FormStep keeps initial values by reference and merges the saved answers over them.
          type: 'slot',
          key: 'publishing_access',
          label: 'Site access',
          initial_value: null,
          options: [
            { key: 'granted', label: 'I granted access to devs@pageoptimizer.pro' },
            { key: 'here', label: 'Share the login details here' },
            { key: 'later', label: 'I will send them later' },
          ],
          show_if: { key: 'need_assistance_publishing', value: 'yes' },
        },
        {
          // Website Publishing Agreement box (PublishingStep.vue renders it through the FormStep slot
          // `field-publishing_agreement_accepted`); the backend stamps the signature on the site.
          type: 'slot',
          key: 'publishing_agreement_accepted',
          label: 'Website Publishing Agreement accepted',
          initial_value: false,
          validation: { accepted: acceptedWhen('need_assistance_publishing', 'yes') },
          show_if: { key: 'need_assistance_publishing', value: 'yes' },
        },
      ],
    },
  ]
}
